import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BsStarFill, BsStar } from "react-icons/bs";
import LeaveCommentIcon from "../../icons/Edit.png";
import "../../styles/ProductTabs.css";

function textToHtml(text = "") {
  return String(text || "")
    .trim()
    .replace(/\n/g, "<br />");
}

function renderRating(rating = 0) {
  const r = Math.round(Number(rating || 0));

  return [1, 2, 3, 4, 5].map((i) =>
    i <= r ? (
      <BsStarFill key={i} className="ratingStarFilled" />
    ) : (
      <BsStar key={i} className="ratingStarEmpty" />
    )
  );
}

function formatReviewDate(value, lang = "uk") {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(lang === "en" ? "en-US" : "uk-UA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export default function ProductTabs({
  activeTab,
  onChangeTab,
  product,
  deliveryLines,
  reviews = [],
  reviewsLoading = false,
  onOpenLeaveComment,
  isLoggedIn = false,
}) {
  const { t, i18n } = useTranslation("product");

  const code = useMemo(() => {
    const v = product?.variant_id || product?.product_id || product?.id;
    return v ? String(v) : "";
  }, [product]);

  const lines = deliveryLines?.length
    ? deliveryLines
    : t("delivery_text", { returnObjects: true });

  const infoHtml = useMemo(() => {
    const text = String(product?.description_text || "").trim();
    if (text) return textToHtml(text);

    const html = String(product?.description_html || "").trim();
    if (html) return html;

    return "";
  }, [product]);

  const lang = (i18n.resolvedLanguage || i18n.language || "uk").startsWith("en")
    ? "en"
    : "uk";

  const ratingCount = Array.isArray(reviews) ? reviews.length : 0;

  const ratingAvg = ratingCount
    ? reviews.reduce((sum, review) => sum + Number(review?.rating || 0), 0) /
      ratingCount
    : 0;

  const indicatorClass =
    activeTab === "delivery"
      ? "isDelivery"
      : activeTab === "reviews"
      ? "isReviews"
      : "isInfo";

  return (
    <>
      <div className={`psTabs psTabs--${activeTab}`}>
        <button
          type="button"
          className={`psTab ${activeTab === "info" ? "active" : ""}`}
          onClick={() => onChangeTab?.("info")}
        >
          {t("tab_info")}
        </button>

        <button
          type="button"
          className={`psTab ${activeTab === "delivery" ? "active" : ""}`}
          onClick={() => onChangeTab?.("delivery")}
        >
          {t("tab_delivery")}
        </button>

        <button
          type="button"
          className={`psTab ${activeTab === "reviews" ? "active" : ""}`}
          onClick={() => onChangeTab?.("reviews")}
        >
          {t("tab_reviews", { count: ratingCount })}
        </button>

        <span className="psTabIndicator" />
      </div>

      <div key={activeTab} className={`psTabContent psTabContent--${activeTab}`}>
        {activeTab === "info" && (
          <>
            {infoHtml ? (
              <div
                className="psDesc"
                dangerouslySetInnerHTML={{ __html: infoHtml }}
              />
            ) : (
              <p className="psDescEmpty">{t("description_empty")}</p>
            )}

            {!!code && (
              <div className="psInfoMeta">
                <span className="psInfoMetaLabel">{t("product_code")}</span>
                <span className="psInfoMetaValue">{code}</span>
              </div>
            )}
          </>
        )}

        {activeTab === "delivery" && (
          <div className="psDelivery">
            {lines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        )}

        {activeTab === "reviews" && (
          <div className="psReviews">
            <div className="psReviewsSummary">
              <div className="psReviewsScore">{ratingAvg.toFixed(1)}</div>
              <div className="psReviewsMeta">
                <div className="productRatingStars">
                  {renderRating(ratingAvg)}
                </div>
                <div className="psReviewsCount">
                  {t("reviews_count", { count: ratingCount })}
                </div>
              </div>
            </div>

            <div className="psReviewsActions">
              <button
                type="button"
                className="psLeaveCommentBtn"
                onClick={() => onOpenLeaveComment?.()}
              >
                <img
                  src={LeaveCommentIcon}
                  className="psLeaveCommentIcon"
                  alt=""
                />

                <span className="psLeaveCommentText">
                  {isLoggedIn
                    ? t("leave_comment")
                    : t("sign_in_to_leave_comment")}
                </span>
              </button>
            </div>

            {reviewsLoading ? (
              <div className="psReviewsLoading">{t("reviews_loading")}</div>
            ) : reviews.length ? (
              <div className="psReviewsList">
                {reviews.map((review) => (
                  <div key={review.id} className="psReviewCard">
                    <div className="psReviewHead">
                      <div className="psReviewAuthor">{review.author}</div>
                      <div className="psReviewDate">
                        {formatReviewDate(review.created_at, lang)}
                      </div>
                    </div>

                    <div className="productRatingStars">
                      {renderRating(review.rating)}
                    </div>

                    <div className="psReviewText">{review.comment}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="psReviewsEmpty">{t("reviews_empty")}</div>
            )}
          </div>
        )}
      </div>
    </>
  );
}